import React, { useState, useRef, useEffect, useCallback, useContext } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  FormControl,
  Alert,
  Divider,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  LinearProgress,
  IconButton,
  Tooltip,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormLabel
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { useDropzone } from 'react-dropzone';
import {
  FaDownload,
  FaFilePdf,
  FaInfo,
  FaTag
} from 'react-icons/fa';
import { CloudUpload, CheckCircle, Clear, Description, Visibility, GetApp } from '@mui/icons-material';
import API_BASE_URL, { API_ENDPOINTS, apiCall } from '../config/api';
import { NotificationContext } from '../context/NotificationContext';
import { useProcess } from '../context/ProcessContext';
import { useActivityLog } from '../context/ActivityLogContext';
import { formatLogTime } from '../utils/dateUtils';
import FavoriteButton from '../components/FavoriteButton';
import { TOOL_COLORS, getCardBackground } from '../constants/colors';

const MasterRetailRugs = () => {
  const theme = useTheme();
  const { showNotification } = useContext(NotificationContext);
  const { addProcess, updateProcess, removeProcess } = useProcess();
  const {
    addActivityProcess,
    updateActivityProcess,
    setLogs,
    addLog: contextAddLog,
    completeActivityProcess,
    setIntervalRef,
    clearIntervalRef,
  } = useActivityLog();

  // Refs for async processing
  const intervalRef = useRef(null);
  const processIdRef = useRef(null);
  const downloadTriggeredRef = useRef(false);

  // Helper function to add logs to activity context
  const addLog = (processId, message) => {
    const formattedMessage = `[${formatLogTime()}] ${message}`;
    contextAddLog(processId, formattedMessage);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        if (processIdRef.current) {
          clearIntervalRef(processIdRef.current);
        }
      }
    };
  }, [clearIntervalRef]);

  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [results, setResults] = useState(null);
  const [progressPercentage, setProgressPercentage] = useState(0);

  // Form state - matching desktop RugGalleryLabels
  const [reportType, setReportType] = useState('Safavieh');
  const [priceFormat, setPriceFormat] = useState('MAP');
  const [importType, setImportType] = useState('Design');

  const reportTypes = [
    { value: 'Safavieh', label: 'Safavieh' },
    { value: 'MarthaStewart', label: 'Martha Stewart' },
    { value: 'RalphLaurenHome', label: 'Ralph Lauren Home' },
    { value: 'LaurenRalphLauren', label: 'Lauren Ralph Lauren' },
    { value: 'MarketRLR', label: 'Market RLR' },
    { value: 'MarketLRL', label: 'MarketLRL' },
    { value: 'MarketMSR', label: 'Market MSR' },
    { value: 'MarketSaf', label: 'Market Safavieh' }
  ];

  const priceFormats = [
    { value: 'MAP', label: 'MAP' },
    { value: 'MSRP', label: 'MSRP' },
    { value: 'Retail', label: 'Retail' },
    { value: 'Designer', label: 'Designer' }
  ];

  const importTypes = [
    { value: 'Design', label: 'Design ID' },
    { value: 'WebID', label: 'Web ID' },
    { value: 'ItemID', label: 'ItemID' }
  ];

  // File handling
  const onDrop = useCallback((acceptedFiles) => {
    const validFiles = acceptedFiles.filter(file =>
      file.name.toLowerCase().endsWith('.xlsx') ||
      file.name.toLowerCase().endsWith('.csv') ||
      file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
      file.type === 'text/csv'
    );

    if (validFiles.length !== acceptedFiles.length) {
      showNotification('Only Excel (.xlsx) and CSV files are supported', 'warning');
    }

    if (validFiles.length === 0) return;

    const newFiles = validFiles.map(file => ({
      id: Math.random().toString(36).substr(2, 9),
      file,
      name: file.name,
      size: file.size,
      status: 'ready'
    }));

    setFiles(newFiles);
    setError('');
  }, [showNotification]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'text/csv': ['.csv']
    },
    multiple: false
  });

  const removeFile = (id) => {
    setFiles(files => files.filter(f => f.id !== id));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleDownloadTemplate = async () => {
    try {
      setLoading(true);
      const response = await fetch(API_ENDPOINTS.RUG_GALLERY_LABELS_SAMPLE(importType), {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('jwtToken')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to download template');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `RugGallery_${importType}_Sample.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setSuccess('Template downloaded successfully');
    } catch (error) {
      setError('Failed to download template');
      console.error('Download error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch status from server and update activity log
  const fetchStatus = async (currentProcessId, activityProcessId, uiProcessId) => {
    try {
      const response = await apiCall(API_ENDPOINTS.RUG_GALLERY_LABELS_STATUS(currentProcessId), {
        method: 'GET',
      });

      // Update activity log with server logs
      if (response.logs && response.logs.length > 0) {
        setLogs(activityProcessId, response.logs);
      }

      // Update progress
      if (response.progressPercentage !== undefined) {
        setProgressPercentage(response.progressPercentage);
        updateActivityProcess(activityProcessId, { progressPercentage: response.progressPercentage });
        updateProcess(uiProcessId, {
          progress: response.progressPercentage,
          status: response.status === 'completed' ? 'completed' : 'Generating labels...'
        });
      }

      // Check if completed or errored
      if (response.status === 'completed') {
        // Stop polling
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
          clearIntervalRef(activityProcessId);
        }

        // Auto-download if not already triggered
        if (!downloadTriggeredRef.current) {
          downloadTriggeredRef.current = true;
          await downloadFile(currentProcessId, activityProcessId, response.fileName);
        }

        // Update file status to completed
        setFiles(prev => prev.map(f => ({ ...f, status: 'completed' })));

        setSuccess(`Labels generated successfully! ${response.processedItems || 0} items processed.`);
        setResults({
          successCount: response.processedItems || 0,
          errorCount: response.failedItems || 0,
          fileName: response.fileName
        });
        completeActivityProcess(activityProcessId, true);
        setProcessing(false);
        setLoading(false);

        setTimeout(() => {
          removeProcess(uiProcessId);
        }, 3000);
      } else if (response.status === 'error') {
        // Stop polling
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
          clearIntervalRef(activityProcessId);
        }

        setError(response.message || 'Processing failed. Please check the activity log for details.');
        setFiles(prev => prev.map(f => ({ ...f, status: 'error' })));
        completeActivityProcess(activityProcessId, false);
        setProcessing(false);
        setLoading(false);

        setTimeout(() => {
          removeProcess(uiProcessId);
        }, 5000);
      }
    } catch (err) {
      console.error('Error fetching status:', err);
    }
  };

  // Download file from server
  const downloadFile = async (currentProcessId, activityProcessId, fileName) => {
    try {
      const response = await fetch(API_ENDPOINTS.RUG_GALLERY_LABELS_DOWNLOAD(currentProcessId), {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('jwtToken')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to download file');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName || 'RugGalleryLabels.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      addLog(activityProcessId, `Downloaded: ${fileName}`);
    } catch (err) {
      console.error('Error downloading file:', err);
      addLog(activityProcessId, `Error downloading file: ${err.message}`);
    }
  };

  const handleProcessLabels = async () => {
    // Validation
    if (files.length === 0) {
      setError('Please select a file to process');
      return;
    }

    setProcessing(true);
    setLoading(true);
    setError('');
    setSuccess('');
    setResults(null);
    setProgressPercentage(0);
    downloadTriggeredRef.current = false;

    // Generate a unique process ID for activity log
    const activityProcessId = `rug-gallery-labels-${Date.now()}`;

    // Add to global activity log
    addActivityProcess(activityProcessId, `Rug Gallery Labels (${reportType})`, 'Master Retail Rugs');
    addLog(activityProcessId, `Starting Rug Gallery Labels processing...`);
    addLog(activityProcessId, `Report Type: ${reportType}`);
    addLog(activityProcessId, `Price Format: ${priceFormat}`);
    addLog(activityProcessId, `Import Type: ${importType}`);
    addLog(activityProcessId, `File: ${files[0].name}`);

    const uiProcessId = addProcess({
      name: 'Rug Gallery Labels',
      status: 'processing',
      progress: 0
    });

    try {
      const formData = new FormData();
      formData.append('file', files[0].file);
      formData.append('lookupType', importType);
      formData.append('reportType', reportType);
      formData.append('priceFormat', priceFormat);

      addLog(activityProcessId, `Uploading file to server...`);
      updateActivityProcess(activityProcessId, { progressPercentage: 10 });
      updateProcess(uiProcessId, {
        progress: 10,
        status: 'Uploading file...'
      });

      // Update file status to processing
      setFiles(prev => prev.map(f => ({ ...f, status: 'processing' })));

      // Make API call to start processing
      const response = await apiCall(API_ENDPOINTS.RUG_GALLERY_LABELS_PROCESS, {
        method: 'POST',
        body: formData,
      });

      if (response.processId) {
        const serverProcessId = response.processId;
        processIdRef.current = serverProcessId;

        addLog(activityProcessId, `Processing started on server...`);

        // Start polling for status
        intervalRef.current = setInterval(() => {
          fetchStatus(serverProcessId, activityProcessId, uiProcessId);
        }, 1000);
        setIntervalRef(activityProcessId, intervalRef.current);
      } else {
        throw new Error('No process ID returned from server');
      }

    } catch (error) {
      console.error('Processing error:', error);
      updateProcess(uiProcessId, {
        status: 'error',
        progress: 0
      });

      addLog(activityProcessId, `Error: ${error.message || 'Failed to process labels'}`);
      completeActivityProcess(activityProcessId, false);

      setError('Failed to process labels. Please check the file format and try again.');
      setFiles(prev => prev.map(f => ({ ...f, status: 'error' })));
      setProcessing(false);
      setLoading(false);

      setTimeout(() => {
        removeProcess(uiProcessId);
      }, 5000);
    }
  };

  const resetForm = () => {
    setFiles([]);
    setError('');
    setSuccess('');
    setResults(null);
    setProgressPercentage(0);
    setReportType('Safavieh');
    setPriceFormat('MAP');
    setImportType('Design');
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: theme.palette.background.default }}>
      <Box sx={{ p: 3, maxWidth: 1600, mx: 'auto', position: 'relative' }}>
        <FavoriteButton interfaceName="Master Retail Rugs" category="pdf" />
        <Typography
          variant="h5"
          gutterBottom
          sx={{
            color: theme.palette.text.primary,
            textAlign: 'center',
            fontWeight: 600,
            letterSpacing: '0.5px',
            borderBottom: `2px solid ${theme.palette.divider}`,
            pb: 2,
            mb: 4
          }}
        >
          MASTER RETAIL RUGS - RUG GALLERY LABELS
        </Typography>

        {/* Alerts */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Main Content */}
          <Grid item xs={12} md={9}>
            <Paper elevation={1} sx={{ p: 3 }}>
              {/* Report Type Selection */}
              <Card sx={{ mb: 3, bgcolor: getCardBackground(theme) }}>
                <CardContent>
                  <FormControl component="fieldset" fullWidth>
                    <FormLabel
                      component="legend"
                      sx={{
                        fontWeight: 'bold',
                        fontSize: '1rem',
                        color: theme.palette.text.primary,
                        mb: 2,
                        '&.Mui-focused': { color: theme.palette.text.primary }
                      }}
                    >
                      Report Type
                    </FormLabel>
                    <RadioGroup
                      row
                      value={reportType}
                      onChange={(e) => setReportType(e.target.value)}
                      sx={{ gap: 1 }}
                    >
                      {reportTypes.map((type) => (
                        <FormControlLabel
                          key={type.value}
                          value={type.value}
                          control={<Radio sx={{ '&.Mui-checked': { color: TOOL_COLORS.PDF.primary } }} />}
                          label={type.label}
                          sx={{ mr: 3 }}
                        />
                      ))}
                    </RadioGroup>
                  </FormControl>
                </CardContent>
              </Card>

              {/* Price Format Selection */}
              <Card sx={{ mb: 3, bgcolor: getCardBackground(theme) }}>
                <CardContent>
                  <FormControl component="fieldset" fullWidth>
                    <FormLabel
                      component="legend"
                      sx={{
                        fontWeight: 'bold',
                        fontSize: '1rem',
                        color: theme.palette.text.primary,
                        mb: 2,
                        '&.Mui-focused': { color: theme.palette.text.primary }
                      }}
                    >
                      Price Format
                    </FormLabel>
                    <RadioGroup
                      row
                      value={priceFormat}
                      onChange={(e) => setPriceFormat(e.target.value)}
                      sx={{ gap: 1 }}
                    >
                      {priceFormats.map((format) => (
                        <FormControlLabel
                          key={format.value}
                          value={format.value}
                          control={<Radio sx={{ '&.Mui-checked': { color: TOOL_COLORS.PDF.primary } }} />}
                          label={format.label}
                          sx={{ mr: 3 }}
                        />
                      ))}
                    </RadioGroup>
                  </FormControl>
                </CardContent>
              </Card>

              {/* Import Type Selection */}
              <Card sx={{ mb: 3, bgcolor: getCardBackground(theme) }}>
                <CardContent>
                  <FormControl component="fieldset" fullWidth>
                    <FormLabel
                      component="legend"
                      sx={{
                        fontWeight: 'bold',
                        fontSize: '1rem',
                        color: theme.palette.text.primary,
                        mb: 2,
                        '&.Mui-focused': { color: theme.palette.text.primary }
                      }}
                    >
                      Import Type
                    </FormLabel>
                    <RadioGroup
                      row
                      value={importType}
                      onChange={(e) => setImportType(e.target.value)}
                      sx={{ gap: 1 }}
                    >
                      {importTypes.map((type) => (
                        <FormControlLabel
                          key={type.value}
                          value={type.value}
                          control={<Radio sx={{ '&.Mui-checked': { color: TOOL_COLORS.PDF.primary } }} />}
                          label={type.label}
                          sx={{ mr: 3 }}
                        />
                      ))}
                    </RadioGroup>
                  </FormControl>
                </CardContent>
              </Card>

              {/* Import File Section */}
              <Card sx={{ mb: 3, bgcolor: 'transparent' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Description sx={{ color: TOOL_COLORS.PDF.primary }} />
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.text.primary }}>
                      Import {importType === 'Design' ? 'Design ID' : importType} List
                    </Typography>
                    {files.length > 0 && (
                      <Tooltip title={`${files.length} file(s) selected`}>
                        <Visibility sx={{ color: '#4CAF50' }} />
                      </Tooltip>
                    )}
                  </Box>

                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 3 }}>
                    Upload an Excel or CSV file containing the list of {importType === 'Design' ? 'design IDs' : importType.toLowerCase() + 's'}.
                    The file should have a column named "{importType === 'Design' ? 'DESIGN' : importType}" with the values to look up.
                  </Typography>

                  {/* Drag & Drop Zone */}
                  <Box
                    {...getRootProps()}
                    sx={{
                      border: `2px dashed ${isDragActive ? TOOL_COLORS.PDF.primary : files.length > 0 ? '#4CAF50' : theme.palette.divider}`,
                      borderRadius: 2,
                      p: 3,
                      textAlign: 'center',
                      bgcolor: isDragActive
                        ? TOOL_COLORS.PDF.light
                        : files.length > 0
                          ? (theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.15)' : 'rgba(76, 175, 80, 0.06)')
                          : 'transparent',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      mb: 2,
                      '&:hover': {
                        borderColor: files.length > 0 ? '#4CAF50' : TOOL_COLORS.PDF.primary,
                        bgcolor: files.length > 0
                          ? (theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.2)' : 'rgba(76, 175, 80, 0.1)')
                          : TOOL_COLORS.PDF.light,
                      }
                    }}
                  >
                    <input {...getInputProps()} />
                    {files.length === 0 ? (
                      <>
                        <CloudUpload sx={{ fontSize: 48, color: isDragActive ? TOOL_COLORS.PDF.primary : theme.palette.text.secondary, mb: 1 }} />
                        <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                          {isDragActive ? 'Drop file here' : 'Drag & drop Excel/CSV file here or click to select'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Supports Excel (.xlsx) and CSV files
                        </Typography>
                      </>
                    ) : (
                      <>
                        <CheckCircle sx={{ fontSize: 48, color: '#4CAF50', mb: 1 }} />
                        <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                          {files.length} file(s) selected
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Click or drop to replace
                        </Typography>
                      </>
                    )}
                  </Box>

                  {/* File List */}
                  {files.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      {files.map((fileData) => (
                        <Box
                          key={fileData.id}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            p: 1,
                            mb: 1,
                            bgcolor: 'background.paper',
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 1
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                            <Description sx={{ color: TOOL_COLORS.PDF.primary, fontSize: 20 }} />
                            <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {fileData.name}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary', flexShrink: 0 }}>
                              ({formatFileSize(fileData.size)})
                            </Typography>
                            {fileData.status === 'processing' && <Chip label="processing" size="small" color="primary" sx={{ ml: 1 }} />}
                            {fileData.status === 'completed' && <Chip label="completed" size="small" color="success" sx={{ ml: 1 }} />}
                            {fileData.status === 'error' && <Chip label="error" size="small" color="error" sx={{ ml: 1 }} />}
                          </Box>
                          {fileData.status === 'ready' && (
                            <IconButton size="small" onClick={() => removeFile(fileData.id)} sx={{ color: theme.palette.error.main }}>
                              <Clear fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      ))}
                    </Box>
                  )}

                  <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                    <Button
                      variant="outlined"
                      onClick={handleDownloadTemplate}
                      disabled={loading}
                      startIcon={<GetApp sx={{ fontSize: '18px !important' }} />}
                      sx={{
                        borderColor: alpha(theme.palette.text.primary, 0.2),
                        color: alpha(theme.palette.text.primary, 0.7),
                        bgcolor: 'transparent',
                        textTransform: 'none',
                        fontWeight: 500,
                        px: 2.5,
                        py: 0.8,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          borderColor: alpha(theme.palette.text.primary, 0.4),
                          color: theme.palette.text.primary,
                          bgcolor: alpha(theme.palette.text.primary, 0.05),
                        },
                        '&:active': {
                          transform: 'scale(0.98)',
                        },
                      }}
                    >
                      Download Template
                    </Button>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                    <Button
                      variant="contained"
                      onClick={handleProcessLabels}
                      disabled={files.length === 0 || processing}
                      sx={{
                        bgcolor: TOOL_COLORS.PDF.primary,
                        '&:hover': { bgcolor: TOOL_COLORS.PDF.hover },
                        px: 4
                      }}
                    >
                      {processing ? 'Generating Labels...' : 'Generate Labels'}
                    </Button>

                    {(files.length > 0 || results) && (
                      <Button
                        variant="outlined"
                        onClick={resetForm}
                        disabled={processing}
                        sx={{
                          borderColor: theme.palette.divider,
                          color: theme.palette.text.primary,
                          '&:hover': {
                            borderColor: theme.palette.text.secondary,
                            bgcolor: alpha(theme.palette.text.primary, 0.05)
                          }
                        }}
                      >
                        Clear
                      </Button>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Paper>
          </Grid>

          {/* Status & Instructions Panel */}
          <Grid item xs={12} md={3}>
            <Paper elevation={1} sx={{ p: 3, position: 'sticky', top: 20 }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                Status & Information
              </Typography>

              {/* Progress */}
              {processing && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ mb: 1, color: theme.palette.text.secondary }}>
                    Generating rug gallery labels...
                  </Typography>
                  <LinearProgress
                    variant={progressPercentage > 0 ? "determinate" : "indeterminate"}
                    value={progressPercentage}
                    sx={{ '& .MuiLinearProgress-bar': { bgcolor: TOOL_COLORS.PDF.primary } }}
                  />
                  {progressPercentage > 0 && (
                    <Typography variant="caption" sx={{ mt: 0.5, display: 'block', color: theme.palette.text.secondary }}>
                      {progressPercentage.toFixed(0)}% complete
                    </Typography>
                  )}
                </Box>
              )}

              {/* Error Alert */}
              {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
                  {error}
                </Alert>
              )}

              {/* Success Alert */}
              {success && (
                <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
                  {success}
                </Alert>
              )}

              {/* Results Summary */}
              {results && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: theme.palette.text.primary }}>
                    Processing Results:
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon>
                        <FaTag color={TOOL_COLORS.PDF.primary} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Items Processed"
                        secondary={`${results.successCount} item(s) successfully processed`}
                        primaryTypographyProps={{ color: theme.palette.text.primary }}
                        secondaryTypographyProps={{ color: theme.palette.text.secondary }}
                      />
                    </ListItem>
                    {results.errorCount > 0 && (
                      <ListItem>
                        <ListItemIcon>
                          <FaTag color={theme.palette.error.main} />
                        </ListItemIcon>
                        <ListItemText
                          primary="Items Not Found"
                          secondary={`${results.errorCount} item(s) not matched`}
                          primaryTypographyProps={{ color: theme.palette.text.primary }}
                          secondaryTypographyProps={{ color: theme.palette.text.secondary }}
                        />
                      </ListItem>
                    )}
                    <ListItem>
                      <ListItemIcon>
                        <FaFilePdf color={TOOL_COLORS.PDF.primary} />
                      </ListItemIcon>
                      <ListItemText
                        primary="PDF Generated"
                        secondary={results.fileName || 'RugGalleryLabels.pdf'}
                        primaryTypographyProps={{ color: theme.palette.text.primary }}
                        secondaryTypographyProps={{ color: theme.palette.text.secondary }}
                      />
                    </ListItem>
                  </List>
                  <Alert severity="info" sx={{ mt: 2 }}>
                    Labels have been automatically downloaded. Check your downloads folder.
                  </Alert>
                </Box>
              )}

              <Divider sx={{ my: 3 }} />

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <FaInfo color={TOOL_COLORS.PDF.primary} />
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.text.primary }}>
                  INSTRUCTIONS
                </Typography>
              </Box>

              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                REPORT TYPES:
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', mb: 1, color: theme.palette.text.secondary }}>
                <strong>Safavieh:</strong> Standard Safavieh label format
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', mb: 1, color: theme.palette.text.secondary }}>
                <strong>Martha Stewart:</strong> Martha Stewart branded labels
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', mb: 1, color: theme.palette.text.secondary }}>
                <strong>Ralph Lauren Home:</strong> RL Home label format
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', mb: 1, color: theme.palette.text.secondary }}>
                <strong>Lauren Ralph Lauren:</strong> LRL label format
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', mb: 1, color: theme.palette.text.secondary }}>
                <strong>Market RLR/LRL:</strong> Market-specific formats
              </Typography>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                PRICE FORMAT:
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', mb: 1, color: theme.palette.text.secondary }}>
                <strong>MAP:</strong> Minimum Advertised Price
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', mb: 1, color: theme.palette.text.secondary }}>
                <strong>MSRP:</strong> Manufacturer's Suggested Retail Price
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', mb: 1, color: theme.palette.text.secondary }}>
                <strong>Retail:</strong> Retail price (uses Retail/MAP column)
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', mb: 1, color: theme.palette.text.secondary }}>
                <strong>Designer:</strong> Designer price (uses DesignerPrice column; price column headed "Designer"). For Market RLR / LRL this prints the plain RLR / LRL Designer label.
              </Typography>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                IMPORT TYPES:
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', mb: 1, color: theme.palette.text.secondary }}>
                <strong>Design ID:</strong> Match by design/collection code
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', mb: 1, color: theme.palette.text.secondary }}>
                <strong>Web ID:</strong> Match by web/online identifier
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', mb: 1, color: theme.palette.text.secondary }}>
                <strong>ItemID:</strong> Match by internal item identifier
              </Typography>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                FEATURES:
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', mb: 1, color: theme.palette.text.secondary }}>
                - Automatic sorting by rug size (width x height)
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', mb: 1, color: theme.palette.text.secondary }}>
                - Runners identified and sorted separately
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', mb: 1, color: theme.palette.text.secondary }}>
                - PDF labels with item details and pricing
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', color: theme.palette.text.secondary }}>
                - Matches against Items Detail master file from S3
              </Typography>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: theme.palette.text.primary }}>
                Generated Output:
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Chip icon={<FaFilePdf color={TOOL_COLORS.PDF.primary} />} label="Rug Gallery Labels PDF" variant="outlined" size="small" sx={{ borderColor: TOOL_COLORS.PDF.primary, color: TOOL_COLORS.PDF.primary }} />
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default MasterRetailRugs;
